import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Modal, PlusIcon, TrashIcon } from "@fawkes/ui";

// ── Voedingsplan uit onderdelen (Alma, Roze, 2026-10-09) ────────────────────────────────────────────
//
// Hoeveel brok en welke, hoeveel natvoer en welke, hoeveel vocht en welk, en hoe vaak per dag. Elk product heeft
// een calorische waarde, zodat je ziet hoeveel kcal het dier krijgt en kreeg. Een nieuw plan vervangt het oude;
// het oude blijft als historie staan. "Je wilt zien wat er voorheen gegeven is, en daarop pas je een nieuw plan aan":
// daarom begint het venster met het huidige plan ingevuld.

export type VoedingSoort = "BROK" | "NATVOER" | "VOCHT";
export const VOEDING_SOORTEN: VoedingSoort[] = ["BROK", "NATVOER", "VOCHT"];

export interface VoedingProduct {
  id: number; naam: string; soort: VoedingSoort; kcalPer100: number | null; actief: boolean; positie: number;
  /** Leeg: voor alle locaties. */
  vestigingId?: number | null;
}

/** Een plan in detail: waaruit het bestond en wat er onder dat plan gegeven en gegeten is. */
export interface PlanDetail {
  id: number; activeFrom: string | null; activeTo: string | null; samenvatting: string; maaltijdenPerDag: number | null;
  regels: SamenstellingRegel[]; kcalPerDag: number | null;
  voerbeurten: { fedAt: string | null; gegeven: number | null; gegeten: number | null; gedrag: string | null; opmerking: string | null; initialen: string | null }[];
  gegevenGram: number; gegetenGram: number;
}
export interface SamenstellingRegel {
  soort: VoedingSoort; productId: number | null; productNaam: string; hoeveelheid: number; eenheid: string;
  kcalPer100: number | null;
}
export interface Samenstelling {
  maaltijdenPerDag: number | null; regels: SamenstellingRegel[]; portie: number; kcalPerDag: number | null;
  kcalVolledig: boolean;
}
export interface HistoriePlan {
  id: number; activeFrom: string | null; activeTo: string | null; samenvatting: string; kcalPerDag: number | null;
}

/** Een regel in het venster: een product uit de lijst, of (productId leeg) een eigen naam. */
interface RegelForm { sleutel: number; soort: VoedingSoort; productId: string; eigenNaam: string; hoeveelheid: string }

const EIGEN = "__eigen";
const MAX_OPMERKING = 500;
export const eenheidVan = (soort: VoedingSoort) => (soort === "VOCHT" ? "ml" : "g");
const eenheidLang = (soort: VoedingSoort, t: (k: string) => string) => t(soort === "VOCHT" ? "voedingsschema.ml" : "voedingsschema.gram");
const getal = (n: number) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10).replace(".", ","));


/**
 * Wat een plan per maaltijd en per dag oplevert. kcal telt alleen de onderdelen met een bekende waarde; {@code volledig}
 * zegt of dat ze allemaal waren.
 */
export function rekenPlan(
  maaltijden: number,
  regels: { soort: VoedingSoort; hoeveelheid: number; kcalPer100: number | null }[],
): { gram: number; ml: number; kcal: number | null; volledig: boolean } {
  const gram = regels.filter((r) => r.soort !== "VOCHT").reduce((s, r) => s + r.hoeveelheid, 0);
  const ml = regels.filter((r) => r.soort === "VOCHT").reduce((s, r) => s + r.hoeveelheid, 0);
  const bekend = regels.filter((r) => r.kcalPer100 != null);
  const kcal = bekend.length === 0 ? null
    : Math.round(bekend.reduce((s, r) => s + (r.hoeveelheid * (r.kcalPer100 ?? 0)) / 100, 0) * maaltijden * 10) / 10;
  return { gram, ml, kcal, volledig: bekend.length === regels.length };
}

/** De tijden aangevuld met lege vakjes tot er {@code aantal} staan; wat er al stond blijft. */
const vulAan = (tijden: string[], aantal: number): string[] =>
  tijden.length >= aantal ? tijden : [...tijden, ...Array<string>(aantal - tijden.length).fill("")];

let volgnummer = 0;
const nieuweRegel = (soort: VoedingSoort): RegelForm =>
  ({ sleutel: ++volgnummer, soort, productId: "", eigenNaam: "", hoeveelheid: "" });

/** Een plan van vóór de onderdelen, zoals het als tekst in het schema staat. */
export interface OudPlan { feedType: string | null; brand: string | null; amountText: string | null; feedingTimes: string | null }

/**
 * Een oud tekstplan ("Brok · Royal canin MB · 3× 70 gram · 08:00 / 12:00 / 16:00") als startpunt voor het nieuwe:
 * hoe vaak, hoeveel en welk product, zodat je alleen hoeft aan te passen wat anders wordt.
 */
export function vanOudPlan(p: OudPlan): { maaltijden: number; tijden: string[]; soort: VoedingSoort; naam: string; hoeveelheid: string } {
  const tijden = (p.feedingTimes ?? "").match(/\d{1,2}:\d{2}/g) ?? [];
  const tekst = (p.amountText ?? "").replace(",", ".");
  const keer = tekst.match(/(\d+)\s*[x×*]\s*(\d+(?:\.\d+)?)/i);
  const getalAlleen = tekst.match(/(\d+(?:\.\d+)?)/);
  const maaltijden = Math.min(6, Math.max(1, keer ? Number(keer[1]) : tijden.length || 2));
  const soort: VoedingSoort = /nat|vlees|blik|pate|paté|mousse/i.test(p.feedType ?? "") ? "NATVOER" : "BROK";
  return {
    maaltijden, tijden: tijden.slice(0, maaltijden), soort,
    naam: (p.brand ?? "").trim() || (p.feedType ?? "").trim(),
    hoeveelheid: keer ? keer[2] : getalAlleen ? getalAlleen[1] : "",
  };
}

export function NieuwVoedingsplanDialoog({ animalId, open, huidig, huidigeTekst, oudPlan, bewerkId = null, startTijden, startOpmerking, onClose, onOpgeslagen }: {
  animalId: string;
  open: boolean;
  /** Het plan dat nu geldt in onderdelen, als het die heeft. */
  huidig: Samenstelling | null;
  /** Het plan dat nu geldt als tekst (een plan van vóór de onderdelen), om naast het nieuwe te zien. */
  huidigeTekst: string | null;
  /** Het plan dat nu geldt als tekst, om het nieuwe plan mee voor te vullen. */
  oudPlan?: OudPlan | null;
  /** Gezet: dit schema wordt bewerkt in plaats van dat er een nieuw schema komt. */
  bewerkId?: number | null;
  /** De voertijden en opmerking waarmee het formulier begint (bij bewerken en "op basis van"). */
  startTijden?: string[];
  startOpmerking?: string | null;
  onClose: () => void;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const { data: productData, refetch: herlaadProducten } = useCustom({
    url: `voeding-producten?dierId=${animalId}`, method: "get", queryOptions: { enabled: open },
  });
  const producten: VoedingProduct[] = Array.isArray(productData?.data) ? (productData.data as VoedingProduct[]) : [];
  const { mutate, isLoading: bezig } = useCustomMutation();

  const oud = !huidig && oudPlan ? vanOudPlan(oudPlan) : null;
  const startRegels = (): RegelForm[] =>
    huidig && huidig.regels.length > 0
      ? huidig.regels.map((r) => ({
          sleutel: ++volgnummer, soort: r.soort,
          productId: r.productId != null ? String(r.productId) : EIGEN,
          eigenNaam: r.productId != null ? "" : r.productNaam, hoeveelheid: getal(r.hoeveelheid).replace(",", "."),
        }))
      : oud && oud.naam
        ? [{ sleutel: ++volgnummer, soort: oud.soort, productId: EIGEN, eigenNaam: oud.naam, hoeveelheid: oud.hoeveelheid }]
        : [nieuweRegel("BROK")];
  const startMaaltijden = huidig?.maaltijdenPerDag ?? oud?.maaltijden ?? 2;
  const [maaltijden, setMaaltijden] = useState<number>(startMaaltijden);
  const [regels, setRegels] = useState<RegelForm[]>(startRegels);
  // Zoveel voertijden als maaltijden: bij 4× staan er meteen vier tijdvakjes klaar (Maarten, 2026-10-12).
  const beginTijden = vulAan(startTijden ?? oud?.tijden ?? [], startMaaltijden);
  const kiesMaaltijden = (n: number) => {
    setMaaltijden(n);
    setTijden((ts) => vulAan(ts.filter((x, i) => i < n || x !== "").slice(0, Math.max(n, 0)), n));
  };
  const [tijden, setTijden] = useState<string[]>(beginTijden);
  const [opmerking, setOpmerking] = useState(startOpmerking ?? "");
  const [fout, setFout] = useState<string | null>(null);
  // Per regel een nieuw product dat je meteen in de lijst zet: naam en kcal.
  const [kcalNieuw, setKcalNieuw] = useState<Record<number, string>>({});
  // Elke keer dat het venster opengaat begint het bij het plan dat nu geldt.
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setMaaltijden(startMaaltijden); setRegels(startRegels()); setTijden(beginTijden); setOpmerking(startOpmerking ?? "");
      setFout(null); setKcalNieuw({});
    }
  }

  const productVan = (r: RegelForm) => producten.find((p) => String(p.id) === r.productId) ?? null;
  const berekend = rekenPlan(maaltijden, regels
    .filter((r) => Number(r.hoeveelheid) > 0)
    .map((r) => ({ soort: r.soort, hoeveelheid: Number(r.hoeveelheid), kcalPer100: productVan(r)?.kcalPer100 ?? null })));

  const wijzig = (sleutel: number, deel: Partial<RegelForm>) =>
    setRegels((rs) => rs.map((r) => (r.sleutel === sleutel ? { ...r, ...deel } : r)));

  const opslaan = () => {
    setFout(null);
    const verzoek = regels.map((r) => ({
      soort: r.soort,
      productId: r.productId && r.productId !== EIGEN ? Number(r.productId) : null,
      productNaam: r.productId === EIGEN ? r.eigenNaam.trim() : null,
      hoeveelheid: Number(r.hoeveelheid.replace(",", ".")),
    }));
    if (verzoek.length === 0 || verzoek.some((r) => !(r.hoeveelheid > 0) || (r.productId == null && !r.productNaam))) {
      setFout(t("voedingsplan.onvolledig"));
      return;
    }
    mutate(
      {
        url: bewerkId != null ? `animals/${animalId}/feeding/plannen/${bewerkId}` : `animals/${animalId}/feeding/plannen`,
        method: bewerkId != null ? "put" : "post",
        values: { maaltijdenPerDag: maaltijden, regels: verzoek, voertijden: tijden.filter(Boolean), note: opmerking.trim() || null },
        successNotification: false, errorNotification: false,
      },
      {
        onSuccess: () => onOpgeslagen(),
        onError: (e) => setFout((e as { message?: string }).message || t("voedingsplan.fout")),
      },
    );
  };

  /** Het nieuwe product van deze regel in de productenlijst zetten, en het meteen kiezen. */
  const productOpslaan = (r: RegelForm) => {
    if (!r.eigenNaam.trim()) return;
    const kcal = (kcalNieuw[r.sleutel] ?? "").trim();
    mutate(
      {
        // Het nieuwe product hoort bij de vestiging van het dier.
        url: `voeding-producten?dierId=${animalId}`, method: "post",
        values: { naam: r.eigenNaam.trim(), soort: r.soort, kcalPer100: kcal === "" ? null : Number(kcal.replace(",", ".")) },
        successNotification: false, errorNotification: false,
      },
      {
        onSuccess: (resp) => {
          const id = (resp?.data as { id?: number } | undefined)?.id;
          void herlaadProducten();
          if (id != null) wijzig(r.sleutel, { productId: String(id) });
        },
        onError: (e) => setFout((e as { message?: string }).message || t("voedingsplan.fout")),
      },
    );
  };

  // Het formulier naar Maartens ontwerp (2026-10-11): één voertype met product, frequentie en portie. De eerste regel
  // is dat hoofdvoer; wat je bij Samenstelling aanvinkt komt er als extra onderdeel onder (Alma: brok + natvoer + vocht).
  const hoofd = regels[0];
  const extra = regels.slice(1);
  const heeft = (s: VoedingSoort) => regels.some((r) => r.soort === s);
  const wisselSoort = (s: VoedingSoort) =>
    setRegels((rs) => (rs.some((r) => r.soort === s) ? rs.filter((r, i) => i === 0 || r.soort !== s) : [...rs, nieuweRegel(s)]));
  const productTekst = (r: RegelForm) => productVan(r)?.naam ?? r.eigenNaam;
  /** Typen is kiezen: een naam die in de lijst staat wordt dat product, iets anders een eigen naam. */
  const zetProductTekst = (r: RegelForm, tekst: string) => {
    const gevonden = producten.find((p) => p.soort === r.soort && p.actief && p.naam.toLowerCase() === tekst.trim().toLowerCase());
    wijzig(r.sleutel, gevonden ? { productId: String(gevonden.id), eigenNaam: "" }
      : { productId: tekst.trim() ? EIGEN : "", eigenNaam: tekst });
  };
  const productVeld = (r: RegelForm) => {
    const product = productVan(r);
    return (
      <div className="vps-product">
        <input id={`vps-product-${r.sleutel}`} list={`vps-lijst-${r.sleutel}`} className="fui-field__input" autoComplete="off"
          aria-label={r === hoofd ? undefined : `${t("voedingsschema.productMerk")} ${t(`voedingsplan.soort.${r.soort}`)}`}
          placeholder={t("voedingsschema.productPlaceholder")} value={productTekst(r)}
          onChange={(e) => zetProductTekst(r, e.target.value)} />
        <datalist id={`vps-lijst-${r.sleutel}`}>
          {producten.filter((p) => p.soort === r.soort && p.actief).map((p) => <option key={p.id} value={p.naam} />)}
        </datalist>
        {product?.kcalPer100 != null && (
          <small className="vps-product__kcal">{getal(product.kcalPer100)} kcal/100 {eenheidVan(r.soort)}</small>
        )}
        {r.productId === EIGEN && r.eigenNaam.trim() !== "" && (
          <div className="vps-product__nieuw">
            <small>{t("voedingsschema.nieuwProduct")}</small>
            <input aria-label={t("voedingsplan.kcalPer100")} inputMode="decimal" className="fui-field__input"
              placeholder={t("voedingsplan.kcalPer100")} value={kcalNieuw[r.sleutel] ?? ""}
              onChange={(e) => setKcalNieuw((k) => ({ ...k, [r.sleutel]: e.target.value }))} />
            <Button variant="secondary" onClick={() => productOpslaan(r)} disabled={bezig}>
              {t("voedingsplan.productBewaren")}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      title={t(bewerkId != null ? "voedingsschema.bewerkTitel" : "voedingsschema.nieuwTitel")}
      subtitle={t(bewerkId != null ? "voedingsschema.bewerkUitleg" : "voedingsschema.nieuwUitleg")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={opslaan} disabled={bezig}>{t(bewerkId != null ? "voedingsschema.wijzigingOpslaan" : "voedingsschema.opslaan")}</Button>
        </>
      }
    >
      <div className="vp vps">
        {bewerkId == null && (huidig || huidigeTekst) && (
          <div className="vp-huidig vps-referentie">
            <span className="vp-huidig__label">{t("voedingsschema.referentie")}</span>
            <span>{huidig ? samenvattingVan(huidig) : huidigeTekst}</span>
            {huidig?.kcalPerDag != null && <span className="vp-kcal">{kcalTekst(huidig.kcalPerDag, huidig.kcalVolledig, t)}</span>}
          </div>
        )}

        <div className="vps-rij">
          <label className="vps-label" htmlFor="vps-voertype">{t("voedingsschema.voertype")}</label>
          <select id="vps-voertype" className="fui-field__input" value={hoofd.soort}
            onChange={(e) => wijzig(hoofd.sleutel, { soort: e.target.value as VoedingSoort, productId: "", eigenNaam: "" })}>
            {VOEDING_SOORTEN.map((s) => <option key={s} value={s}>{t(`voedingsplan.soort.${s}`)}</option>)}
          </select>
        </div>

        <div className="vps-rij">
          <label className="vps-label" htmlFor={`vps-product-${hoofd.sleutel}`}>{t("voedingsschema.productMerk")}</label>
          {productVeld(hoofd)}
        </div>

        <div className="vp-blok">
          <span className="vps-label">{t("voedingsschema.frequentie")} <small>{t("voedingsschema.perDagKlein")}</small></span>
          <div className="vp-keer vps-keer" role="radiogroup" aria-label={t("voedingsplan.hoeVaak")}>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <button key={n} type="button" role="radio" aria-checked={maaltijden === n}
                className={`vp-keer__knop${maaltijden === n ? " is-actief" : ""}`} onClick={() => kiesMaaltijden(n)}>
                {n}×
              </button>
            ))}
          </div>
        </div>

        <div className="vp-blok">
          <label className="vps-label" htmlFor={`vps-portie-${hoofd.sleutel}`}>{t("voedingsplan.portie")}</label>
          <div className="vps-portie">
            <span className="vps-hoeveel">
              <input id={`vps-portie-${hoofd.sleutel}`} inputMode="decimal" className="fui-field__input"
                value={hoofd.hoeveelheid} onChange={(e) => wijzig(hoofd.sleutel, { hoeveelheid: e.target.value })} />
              <span>{eenheidLang(hoofd.soort, t)}</span>
            </span>
            <div className="vps-totaal" role="status">
              <span>{t("voedingsschema.totaalPerDag")}</span>
              <strong>{portieTekst(berekend.gram * maaltijden, berekend.ml * maaltijden)}</strong>
              <small>{berekend.kcal != null ? kcalTekst(berekend.kcal, berekend.volledig, t) : t("voedingsschema.kcalOnbekendKort")}</small>
            </div>
          </div>
        </div>

        <div className="vp-blok">
          <span className="vps-label">{t("voedingsschema.samenstelling")}</span>
          <div className="vps-vinkjes">
            {VOEDING_SOORTEN.map((s) => (
              <label key={s} className="vps-vinkje">
                <input type="checkbox" checked={heeft(s)} disabled={s === hoofd.soort} onChange={() => wisselSoort(s)} />
                {t(`voedingsplan.soort.${s}`)}
              </label>
            ))}
          </div>
          {extra.map((r) => (
            <div key={r.sleutel} className="vps-extra">
              <span className="vps-extra__soort">{t(`voedingsplan.soort.${r.soort}`)}</span>
              {productVeld(r)}
              <span className="vps-hoeveel">
                <input aria-label={`${t("voedingsplan.portie")} ${t(`voedingsplan.soort.${r.soort}`)}`} inputMode="decimal" placeholder={t("voedingsschema.portieKort")}
                  className="fui-field__input" value={r.hoeveelheid}
                  onChange={(e) => wijzig(r.sleutel, { hoeveelheid: e.target.value })} />
                <span>{eenheidLang(r.soort, t)}</span>
              </span>
              <button type="button" className="vp-regel__weg" aria-label={t("voedingsplan.regelWeg")}
                onClick={() => setRegels((rs) => rs.filter((x) => x.sleutel !== r.sleutel))}>
                <TrashIcon width={16} height={16} />
              </button>
            </div>
          ))}
        </div>

        <div className="vp-blok">
          <span className="vps-label">{t("voedingsschema.voertijden")}</span>
          <div className="vps-tijden">
            {tijden.map((tijd, i) => (
              <span key={i} className="vps-tijd">
                <input type="time" step={900} aria-label={`${t("voedingsplan.voertijd")} ${i + 1}`} value={tijd}
                  onChange={(e) => setTijden((ts) => ts.map((x, j) => (j === i ? e.target.value : x)))} />
                <button type="button" aria-label={t("voedingsschema.tijdWeg", { args: [tijd || String(i + 1)] })}
                  onClick={() => setTijden((ts) => ts.filter((_, j) => j !== i))}>×</button>
              </span>
            ))}
            <button type="button" className="vps-tijd-erbij" disabled={tijden.length >= 12}
              onClick={() => setTijden((ts) => [...ts, ""])}>
              <PlusIcon width={14} height={14} /> {t("voedingsschema.tijdToevoegen")}
            </button>
          </div>
        </div>

        <div className="vp-blok">
          <label className="vps-label" htmlFor="vps-opmerking">{t("animalDetailView.feeding.field.note")}</label>
          <div className="vps-opmerking">
            <textarea id="vps-opmerking" className="fui-field__input" rows={3} maxLength={MAX_OPMERKING} value={opmerking}
              onChange={(e) => setOpmerking(e.target.value)} />
            <small>{opmerking.length}/{MAX_OPMERKING}</small>
          </div>
        </div>
        {fout && <span className="fawkes-feeding__form-error" role="alert">{fout}</span>}
      </div>
    </Modal>
  );
}

function portieTekst(gram: number, ml: number): string {
  const delen = [gram > 0 ? `${getal(gram)} g` : null, ml > 0 ? `${getal(ml)} ml` : null].filter(Boolean);
  return delen.length ? delen.join(" + ") : "-";
}

function kcalTekst(kcal: number, volledig: boolean, t: (k: string) => string): string {
  return t(volledig ? "voedingsplan.kcalPerDag" : "voedingsplan.kcalPerDagMinstens").replace("%s", getal(Math.round(kcal)));
}

/** "3× 10 g Gastro-Intestinal + 10 ml Water", zoals op hun kennelkaart. */
export function samenvattingVan(s: Samenstelling): string {
  const delen = s.regels.map((r) => `${getal(r.hoeveelheid)} ${r.eenheid} ${r.productNaam}`).join(" + ");
  return s.maaltijdenPerDag ? `${s.maaltijdenPerDag}× ${delen}` : delen;
}

/**
 * De huidige voergift in de voedingstegel, met een link naar alle schema's van het dier (Voedingsschema's: overzicht,
 * details en de gegeven voedingen per schema).
 */
export function VoedingsplanOverzicht({ samenstelling, aantalEerdere, onOpen }: {
  samenstelling: Samenstelling | null;
  aantalEerdere: number;
  onOpen: () => void;
}) {
  const t = useTranslate();
  if (!samenstelling && aantalEerdere === 0) return null;
  return (
    <div className="vp-overzicht">
      {samenstelling && (
        <div className="vp-overzicht__nu">
          <span className="vp-overzicht__kop">{t("voedingsplan.voergift")}</span>
          <ul>
            {samenstelling.regels.map((r, i) => (
              <li key={i}>
                <span className="vp-overzicht__soort">{t(`voedingsplan.soort.${r.soort}`)}</span>
                {getal(r.hoeveelheid)} {r.eenheid} {r.productNaam}
              </li>
            ))}
          </ul>
          <span className="vp-overzicht__som">
            {samenstelling.maaltijdenPerDag ? t("voedingsplan.keerPerDag").replace("%s", String(samenstelling.maaltijdenPerDag)) + " · " : ""}
            {t("voedingsplan.portie")} {getal(samenstelling.portie)}
            {samenstelling.kcalPerDag != null && ` · ${kcalTekst(samenstelling.kcalPerDag, samenstelling.kcalVolledig, t)}`}
          </span>
        </div>
      )}
      {aantalEerdere > 0 && (
        <button type="button" className="vp-link" onClick={onOpen}>
          {aantalEerdere === 1 ? t("voedingsschema.eenEerder") : t("voedingsschema.eerdere").replace("%s", String(aantalEerdere))} →
        </button>
      )}
    </div>
  );
}
